import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { lastMinuteService } from "../../services/lastMinuteService";
import { useRoster } from "../../hooks/last-minute/useRoster";
import { useMyListedPlayers } from "../../hooks/last-minute/useMyListedPlayers";
import FeeFrameSelect from "../scout-request/FeeFrameSelect";
import CountryBlacklistField from "./CountryBlacklistField";
import ListingCard from "./ListingCard";
import { SerifInfoGlyph, MagnifierGlyph } from "./lmGlyphs";
import { ROLES, ROLE_ROUTES } from "../../config/roles";
import {
  SALARY_BAND_PRESETS,
  feeFrameToSalaryBand,
  salaryBandToFeeFrame,
  urgencyMeta,
  forSaleNeedsOption,
  step1Complete,
  daysLeftShort,
} from "../../utils/lastMinute";
import { STAFF_ROLE_CHOICES, VISIBILITY_OPTIONS, staffRoleLabel } from "../../constants/lastMinuteChoices";

// ── The 4-step listing composer, presentation rebuilt 1:1 from the LM handoff
// (desktop 4a–4f + publish confirmation 4e, mobile 8d–8g). One composer serves
// BOTH entry points (Invariant 13): the page "List a player" (empty) and
// My-Players "List as available" (player prefilled). Also runs in edit mode
// (prefill from an existing listing). Steps: Player · Listing · Discretion ·
// Preview. All symbols (✓ ✕ ← ▲ ▾ dots) are the artboard's unicode marks.
// Functional state + submit payload are untouched.

const STEPS = ["Player", "Listing", "Discretion", "Preview"];
// Mobile step subtitles (8d–8g): "Step N of 4 · {subtitle}".
const STEP_SUBTITLES = ["Who is available?", "The listing", "Who sees what", "Exactly what clubs see"];

// 4b copy, verbatim from the canvas (option cards).
const URGENCY_CARDS = [
  { key: "contract_free", label: "Contract-free", help: "Signable now, even outside windows" },
  { key: "expiring_precontract", label: "Expiring", help: "Running out, open to pre-contract" },
  { key: "for_sale", label: "For sale", help: "Under contract; open to a transfer" },
];

// Artboard switch (4b: 38×22 blue track, 18px knob; 8e: 40×24 / 20px on touch).
const Switch = ({ on, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={() => onChange(!on)}
    className={`relative rounded-full flex-none transition w-[38px] h-[22px] sm:w-[38px] sm:h-[22px] ${
      on ? "bg-[#2283FF]" : "bg-[#D8DFE9]"
    }`}
  >
    <span
      className={`absolute top-[2px] w-[18px] h-[18px] rounded-full bg-white shadow-[0_1px_2px_rgba(16,24,40,.25)] transition ${
        on ? "right-[2px]" : "left-[2px]"
      }`}
    />
  </button>
);

// Field label with the serif-"i" glyph + optional grey suffix (4b/4c grammar).
const FieldLabel = ({ children, info = false, suffix = null }) => (
  <p className="flex items-center gap-[5px] text-[12px] font-semibold text-[#33475E] mb-2">
    {children}
    {info && <SerifInfoGlyph size={13} />}
    {suffix && <span className="font-medium text-[#98A5B8]">{suffix}</span>}
  </p>
);

// Desktop stepper (4a–4d): numbered 22px circles joined by flex-1 connector
// lines; done = blue ✓ circle + #BDD9FF connector, active = blue circle + dark
// label, upcoming = grey circle/label.
const DesktopStepper = ({ current }) => (
  <div className="hidden sm:flex items-center w-full">
    {STEPS.map((label, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <React.Fragment key={label}>
          {i > 0 && <span className={`flex-1 h-[1.5px] mx-2.5 ${i <= current ? "bg-[#BDD9FF]" : "bg-[#E4E9F1]"}`} />}
          <span className="flex items-center gap-[7px]">
            <span
              className={`inline-flex w-[22px] h-[22px] rounded-full items-center justify-center font-bold ${
                done || active ? "bg-[#2283FF] text-white" : "bg-[#EDF1F6] text-[#8A98AC]"
              } ${done ? "text-[10px]" : "text-[11px]"}`}
            >
              {done ? "✓" : i + 1}
            </span>
            <span className={`text-[12px] ${active ? "font-semibold text-[#14263D]" : "font-medium text-[#8A98AC]"}`}>
              {label}
            </span>
          </span>
        </React.Fragment>
      );
    })}
  </div>
);

// Mobile stepper (8d–8g): four 3.5px progress bars + "Step N of 4 · {subtitle}".
const MobileStepper = ({ current }) => (
  <div className="sm:hidden">
    <div className="flex items-center gap-[7px]">
      {STEPS.map((label, i) => (
        <span key={label} className={`flex-1 h-[3.5px] rounded-full ${i <= current ? "bg-[#2283FF]" : "bg-[#E4E9F1]"}`} />
      ))}
    </div>
    <p className="text-[12px] text-[#8A98AC] mt-[7px]">
      Step {current + 1} of {STEPS.length} · <span className="font-semibold text-[#33475E]">{STEP_SUBTITLES[current]}</span>
    </p>
  </div>
);

// Initials avatar for roster rows (the roster serializer has no photos; the
// artboard's 38/40px circle keeps its exact geometry).
const RosterAvatar = ({ name }) => (
  <span className="w-[38px] h-[38px] sm:w-[38px] sm:h-[38px] rounded-full bg-[#EAEFF5] flex items-center justify-center text-[13px] font-bold text-[#5A6B82] flex-none">
    {(name || "?").charAt(0).toUpperCase()}
  </span>
);

// A roster row inside the bordered picker list (4a/8d): selected = tinted row
// with a left accent bar + blue ✓ disc; already-listed rows dim + carry the
// "Already listed · Nd left" pill.
const RosterRow = ({ person, selected, listed, onSelect, meta }) => (
  <button
    type="button"
    onClick={() => onSelect(person)}
    className={`w-full flex items-center gap-3 px-3.5 py-3 text-left border-t border-[#EEF1F6] first:border-t-0 ${
      selected ? "bg-[#F0F7FF] border-l-[3px] border-l-[#2283FF]" : "border-l-[3px] border-l-transparent"
    } ${!selected && listed ? "opacity-55" : ""}`}
  >
    <RosterAvatar name={person.name} />
    <span className="flex-1 min-w-0">
      <span className="block text-[13.5px] font-bold text-[#14263D] truncate">{person.name || "Unnamed"}</span>
      {meta && <span className="block text-[11.5px] text-[#5A6B82] truncate mt-0.5">{meta}</span>}
    </span>
    {selected ? (
      <span className="inline-flex w-[18px] h-[18px] rounded-full bg-[#2283FF] text-white text-[10px] font-bold items-center justify-center flex-none">
        ✓
      </span>
    ) : listed ? (
      <span className="rounded-full bg-[#E6F6EC] px-2 py-[3px] text-[11px] font-semibold text-[#0E7C3C] flex-none whitespace-nowrap">
        Already listed{listed.left ? ` · ${listed.left}` : ""}
      </span>
    ) : (
      <span className="inline-block w-[18px] h-[18px] rounded-full border-[1.5px] border-[#C7D3E2] flex-none" />
    )}
  </button>
);

// Staff-role catalog dropdown (4f — the open state IS designed, so it is built
// exactly as drawn: blue-ring trigger with ▲, menu rows with the selected row
// tinted + ✓). Value semantics identical to the old native select.
const StaffRoleDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`w-full flex items-center rounded-xl px-3.5 py-[11px] text-[13px] font-semibold text-left ${
          open
            ? "border-[1.5px] border-[#2283FF] shadow-[0_0_0_3px_rgba(34,131,255,.1)]"
            : "border border-[#DFE6EF] hover:bg-gray-50"
        } ${value ? "text-[#14263D]" : "text-[#8A98AC]"}`}
      >
        {value ? staffRoleLabel(value) : "Select a role…"}
        <span className="ml-auto text-[10px] text-[#8A98AC]">{open ? "▲" : "▾"}</span>
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute z-30 left-0 right-0 mt-1.5 rounded-xl border border-[#E4E9F1] bg-white shadow-[0_12px_28px_-12px_rgba(16,24,40,.18)] overflow-hidden max-h-[264px] overflow-y-auto"
        >
          {STAFF_ROLE_CHOICES.map((r, i) => {
            const on = value === r.value;
            return (
              <button
                key={r.value}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => {
                  onChange(r.value);
                  setOpen(false);
                }}
                className={`w-full flex items-center px-3.5 py-2.5 text-[12.5px] text-left truncate ${
                  i > 0 ? "border-t border-[#F1F4F8]" : ""
                } ${on ? "bg-[#F0F7FF] font-semibold text-[#1466D8]" : "text-[#33475E] hover:bg-gray-50"}`}
              >
                {r.label}
                {on && <span className="ml-auto">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Which salary preset (if any) the current frame equals (pill highlight, 4b).
const matchingSalaryPresetId = (v) => {
  if (!v || v.fee_frame_kind !== "range") return null;
  const hit = SALARY_BAND_PRESETS.find(
    (p) =>
      (p.payload.fee_frame_currency || "") === (v.fee_frame_currency || "") &&
      (p.payload.fee_frame_min ?? null) === (v.fee_frame_min ?? null) &&
      (p.payload.fee_frame_max ?? null) === (v.fee_frame_max ?? null),
  );
  return hit ? hit.id : null;
};

const ListingComposer = ({ open, onClose, prefillPlayerId = null, editListing = null, onPublished }) => {
  const isEdit = !!editListing;
  const navigate = useNavigate();
  // QAV5-409: the composer is Agent + AgencyAdmin only (routes/index.js
  // "/last-minute/my-listings" comment). "Add … first" used to hard-code the
  // Agent-only /agent/mysquad route for both roles; route AgencyAdmin to its
  // own player management instead (same target InvitePlayer.js already
  // navigates to for an AgencyAdmin after adding a player, ROUTES §agency).
  const { currentRole } = useSelector((state) => state.auth);
  const addPlayersTarget =
    currentRole === ROLES.AgencyAdmin ? "/agency" : ROLE_ROUTES[ROLES.Agent].dashboard;
  const { roster, loading: rosterLoading } = useRoster(open);
  // Marks roster players that are already on Last Minute (4a "Already listed").
  const listedMap = useMyListedPlayers(open && !isEdit);

  const [step, setStep] = useState(0);
  const [listingType, setListingType] = useState("player");
  const [picked, setPicked] = useState(null); // {kind:'player'|'staff'|'coach', id, name, meta}
  const [staffRole, setStaffRole] = useState("");
  const [query, setQuery] = useState("");

  const [urgency, setUrgency] = useState("contract_free");
  const [buy, setBuy] = useState(false);
  const [loan, setLoan] = useState(false);
  const [category, setCategory] = useState("professional");
  const [readyToFly, setReadyToFly] = useState(false);
  const [shortTerm, setShortTerm] = useState(false);
  const [blacklist, setBlacklist] = useState([]);
  const [salaryFrame, setSalaryFrame] = useState(null);
  const [note, setNote] = useState("");

  const [anonymized, setAnonymized] = useState(true);
  const [visibility, setVisibility] = useState("clubs_agents");

  const [submitting, setSubmitting] = useState(false);
  const [published, setPublished] = useState(false);
  // Mobile step-4 preview toggle (8g): "Anonymized · live" / "Named · after reveal".
  const [previewSide, setPreviewSide] = useState("anon");

  // ── (Re)hydrate on open ──
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setPublished(false);
    setPreviewSide("anon");
    if (isEdit) {
      const l = editListing;
      setListingType(l.listing_type || "player");
      setStaffRole(l.staff_role || "");
      setUrgency(l.urgency_class || "contract_free");
      setBuy(!!l.for_sale_buy);
      setLoan(!!l.for_sale_loan);
      setCategory(l.category || "professional");
      setReadyToFly(!!l.ready_to_fly);
      setShortTerm(!!l.short_term);
      setAnonymized(!!l.anonymized);
      setVisibility(l.visibility || "clubs_agents");
      setNote(l.note || "");
      setBlacklist(l.blacklist_country_ids || []);
      setSalaryFrame(salaryBandToFeeFrame(l));
      // Reconstruct the picked subject from the listing snapshot.
      if (l.listing_type === "staff") {
        setPicked(
          l.coach_member_id
            ? { kind: "coach", id: l.coach_member_id, name: l.player_name }
            : { kind: "staff", id: l.staff_member_id, name: l.player_name },
        );
      } else {
        setPicked({ kind: "player", id: l.player_id, name: l.player_name, meta: l.position });
      }
    } else {
      setListingType("player");
      setPicked(null);
      setStaffRole("");
      setUrgency("contract_free");
      setBuy(false);
      setLoan(false);
      setCategory("professional");
      setReadyToFly(false);
      setShortTerm(false);
      setBlacklist([]);
      setSalaryFrame(null);
      setNote("");
      setAnonymized(true);
      setVisibility("clubs_agents");
    }
    setQuery("");
  }, [open, isEdit, editListing]);

  // Prefill a player from the My-Players entry once the roster arrives.
  useEffect(() => {
    if (!open || isEdit || !prefillPlayerId || picked) return;
    // The entry point passes the player's account_id (the key My Players exposes);
    // match on the roster row's account_id (BE follow-up) or its pk, whichever hits.
    const p = (roster.players || []).find(
      (x) => String(x.account_id) === String(prefillPlayerId) || String(x.id) === String(prefillPlayerId),
    );
    if (p) setPicked({ kind: "player", id: p.id, name: p.name, meta: [p.position, p.club].filter(Boolean).join(" · ") });
  }, [open, isEdit, prefillPlayerId, roster.players, picked]);

  const filteredPlayers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (roster.players || []).filter((p) => (q ? (p.name || "").toLowerCase().includes(q) : true));
  }, [roster.players, query]);

  const filteredStaff = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = [
      ...(roster.staff || []).map((s) => ({ ...s, kind: "staff" })),
      ...(roster.coaches || []).map((c) => ({ ...c, kind: "coach" })),
    ];
    return all.filter((p) => (q ? (p.name || "").toLowerCase().includes(q) : true));
  }, [roster.staff, roster.coaches, query]);

  if (!open) return null;

  const forSale = urgency === "for_sale";
  const canContinueStep1 = step1Complete(listingType, picked, staffRole);
  const step2Valid = !forSaleNeedsOption(urgency, buy, loan);

  const buildPayload = () => ({
    listing_type: listingType,
    ...(listingType === "player"
      ? { player_id: picked?.id }
      : {
          staff_role: staffRole,
          ...(picked?.kind === "coach" ? { coach_member_id: picked.id } : { staff_member_id: picked?.id }),
        }),
    urgency_class: urgency,
    for_sale_buy: forSale ? buy : false,
    for_sale_loan: forSale ? loan : false,
    category,
    short_term: shortTerm,
    ready_to_fly: readyToFly,
    anonymized,
    visibility,
    note,
    blacklist_country_ids: blacklist,
    ...feeFrameToSalaryBand(salaryFrame),
  });

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    const payload = buildPayload();
    const res = isEdit
      ? await lastMinuteService.updateListing(editListing.id, payload)
      : await lastMinuteService.createListing(payload);
    setSubmitting(false);
    if (res.success) {
      if (isEdit) {
        toast.success("Listing updated");
        onPublished?.(res.data);
        onClose();
      } else {
        setPublished(true);
        onPublished?.(res.data);
      }
    } else {
      toast.error(res.error || "Could not publish the listing.");
    }
  };

  // A live preview listing object (drives ListingCard for step 4).
  const previewBase = {
    id: -1,
    listing_type: listingType,
    urgency_class: urgency,
    for_sale_buy: buy,
    for_sale_loan: loan,
    category,
    ready_to_fly: readyToFly,
    short_term: shortTerm,
    note,
    position: listingType === "staff" ? staffRoleLabel(staffRole) : picked?.meta || "",
    age: null,
    salary_band: salaryFrame ? undefined : null, // exact band string only comes back from the API
    not_ready_for: [],
    agent: { name: "You", agency: "" },
    created_at: new Date().toISOString(),
    expires_at: new Date(Date.now() + 14 * 864e5).toISOString(),
  };
  const anonPreview = { ...previewBase, is_named: false, market_value: null };
  const namedPreview = { ...previewBase, is_named: true, player_name: picked?.name || "Player", player_club: "", market_value: null };

  const salaryPresetId = matchingSalaryPresetId(salaryFrame);
  const salaryIsCustom = !!salaryFrame && !salaryPresetId;

  // Picked-subject meta line for the step-2 header (4b: "· CM · 23 · Denmark").
  const pickedMeta = listingType === "staff" ? staffRoleLabel(staffRole) : picked?.meta || "";

  // Mobile header title per step (8d–8g). Desktop title only exists on step 1.
  const mobileTitle = published
    ? ""
    : step === 0
    ? isEdit
      ? "Edit listing"
      : "New listing"
    : step === 2
    ? "Discretion & visibility"
    : step === 3
    ? "Preview"
    : "";

  return (
    <div className="fixed inset-0 z-50 flex sm:items-center sm:justify-center bg-[rgba(16,26,40,0.44)] sm:p-4">
      <div
        className={`bg-white w-full flex flex-col max-h-full sm:rounded-[18px] sm:shadow-[0_32px_64px_-16px_rgba(16,24,40,0.35)] sm:max-h-[94vh] ${
          step === 3 && !published ? "sm:max-w-[680px]" : "sm:max-w-[640px]"
        }`}
      >
        {/* Header */}
        {!published && (
          <div className="flex flex-col gap-3.5 px-[18px] pt-4 pb-3.5 sm:p-6 sm:pb-4 border-b border-[#EEF1F6]">
            {/* Title / subject row */}
            {step === 1 && picked ? (
              <div className="flex items-center gap-2.5 min-w-0">
                <RosterAvatar name={picked.name} />
                <p className="text-[14px] font-bold text-[#14263D] truncate">
                  {picked.name}
                  {pickedMeta && <span className="font-medium text-[#8A98AC] text-[12px]"> · {pickedMeta}</span>}
                </p>
                {!isEdit && (
                  <button
                    onClick={() => setStep(0)}
                    className="ml-auto text-[12px] font-semibold text-[#1466D8] hover:underline flex-none"
                  >
                    Change
                  </button>
                )}
                <button
                  onClick={onClose}
                  className={`${isEdit ? "ml-auto" : "ml-1"} text-[14px] text-[#98A5B8] hover:text-[#14263D] flex-none`}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className="flex items-center">
                <h2 className="text-[19px] sm:text-[17px] font-bold text-[#14263D]">
                  <span className="sm:hidden">{mobileTitle}</span>
                  <span className="hidden sm:inline">{isEdit ? "Edit listing" : "New Last Minute listing"}</span>
                </h2>
                <button
                  onClick={onClose}
                  className="ml-auto text-[15px] sm:text-[14px] text-[#98A5B8] hover:text-[#14263D]"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            )}
            <DesktopStepper current={step} />
            <MobileStepper current={step} />
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-[18px] py-4 sm:p-6">
          {published ? (
            // 4e — publish confirmation (buttons live inside the card, centered)
            <div className="flex flex-col items-center text-center gap-2.5 py-8 sm:py-10">
              <div className="w-[54px] h-[54px] rounded-full bg-[#E6F6EC] flex items-center justify-center mb-1">
                <span className="text-[22px] font-bold text-[#0E7C3C]">✓</span>
              </div>
              <h3 className="text-[17px] font-bold text-[#14263D]">Your listing is live</h3>
              <p className="text-[13px] text-[#5A6B82] leading-[1.55] max-w-[320px]">
                Live for <b className="text-[#33475E]">14 days</b>. Extend anytime with one click. Views and contacts
                appear in My Listings, visible only to you.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 mt-3.5 w-full sm:w-auto">
                <button
                  onClick={() => {
                    onClose();
                    navigate("/last-minute");
                  }}
                  className="rounded-[10px] border border-[#D8E0EA] bg-white px-4 py-2.5 text-[13px] font-semibold text-[#33475E] hover:bg-gray-50"
                >
                  View on Last Minute
                </button>
                <button
                  onClick={() => {
                    onClose();
                    navigate("/last-minute/my-listings");
                  }}
                  className="rounded-[10px] bg-[#2283FF] px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-[#1B6FE0]"
                >
                  Go to My Listings
                </button>
              </div>
            </div>
          ) : step === 0 ? (
            // 4a / 4f / 8d — roster picker (own roster only)
            <div>
              {/* Players/Staff switch + roster search (one row on desktop, 4a) */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-3 mb-3.5">
                <div className="flex sm:inline-flex rounded-[10px] bg-[#EAEFF5] p-[3px] flex-none">
                  {["player", "staff"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        setListingType(t);
                        setPicked(null);
                      }}
                      className={`flex-1 sm:flex-none px-4 py-1.5 rounded-[7px] text-[12.5px] text-center ${
                        listingType === t
                          ? "bg-white font-semibold text-[#14263D] shadow-[0_1px_2px_rgba(16,24,40,.08)]"
                          : "font-medium text-[#5A6B82]"
                      }`}
                    >
                      {t === "player" ? "Players" : "Staff"}
                    </button>
                  ))}
                </div>
                <div className="flex-1 flex items-center gap-2 rounded-[10px] bg-[#F1F4F8] sm:bg-white sm:border sm:border-[#DFE6EF] px-3 py-[9px] sm:py-2">
                  <MagnifierGlyph />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={listingType === "player" ? "Search your players" : "Search your staff"}
                    className="w-full bg-transparent text-[12.5px] text-[#14263D] placeholder:text-[#8A98AC] focus:outline-none"
                  />
                </div>
              </div>

              {/* Roster list (bordered container, 4a) */}
              <div className="rounded-[14px] border border-[#E4E9F1] overflow-hidden">
                {rosterLoading ? (
                  <p className="text-[12.5px] text-[#9AA8BC] px-3.5 py-3">Loading your roster…</p>
                ) : listingType === "player" ? (
                  filteredPlayers.length === 0 ? (
                    <p className="text-[12.5px] text-[#9AA8BC] px-3.5 py-3">No players on your roster match.</p>
                  ) : (
                    filteredPlayers.map((p) => (
                      <RosterRow
                        key={p.id}
                        person={p}
                        selected={picked?.kind === "player" && picked.id === p.id}
                        listed={(() => {
                          const hit = listedMap.get(p.account_id) || listedMap.get(p.id);
                          return hit ? { left: daysLeftShort(hit.expires_at) } : null;
                        })()}
                        onSelect={() =>
                          setPicked({ kind: "player", id: p.id, name: p.name, meta: [p.position, p.club].filter(Boolean).join(" · ") })
                        }
                        meta={[p.position, p.club].filter(Boolean).join(" · ")}
                      />
                    ))
                  )
                ) : filteredStaff.length === 0 ? (
                  <p className="text-[12.5px] text-[#9AA8BC] px-3.5 py-3">No staff you represent match.</p>
                ) : (
                  filteredStaff.map((p) => (
                    <RosterRow
                      key={`${p.kind}-${p.id}`}
                      person={p}
                      selected={picked?.kind === p.kind && picked.id === p.id}
                      listed={null}
                      onSelect={() => setPicked({ kind: p.kind, id: p.id, name: p.name, meta: p.role })}
                      meta={p.role}
                    />
                  ))
                )}
              </div>

              {/* Staff-role catalog (4f — designed dropdown) */}
              {listingType === "staff" && picked && (
                <div className="mt-4">
                  <FieldLabel suffix="· from the staff-role catalog">Role</FieldLabel>
                  <StaffRoleDropdown value={staffRole} onChange={setStaffRole} />
                </div>
              )}

              <p className="mt-3.5 text-[12px] text-[#8A98AC] text-center sm:text-left">
                {listingType === "player" ? "Player" : "Person"} not in your list yet?{" "}
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate(addPlayersTarget);
                  }}
                  className="font-semibold text-[#1466D8] hover:underline"
                >
                  Add {listingType === "player" ? "him in My Players" : "them"} first
                </button>
              </p>
            </div>
          ) : step === 1 ? (
            // 4b / 8e — the listing
            <div className="space-y-4">
              <div>
                <FieldLabel>Why is he available?</FieldLabel>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {URGENCY_CARDS.map((o) => {
                    const on = urgency === o.key;
                    const dot = urgencyMeta(o.key).dot;
                    const isSale = o.key === "for_sale";
                    return (
                      <button
                        key={o.key}
                        type="button"
                        onClick={() => setUrgency(o.key)}
                        className={`text-left rounded-xl p-3 flex flex-col gap-[5px] ${
                          on
                            ? "border-[1.5px] border-[#2283FF] bg-[#F7FAFF] shadow-[0_0_0_3px_rgba(34,131,255,.1)]"
                            : "border border-[#DFE6EF] hover:bg-gray-50"
                        }`}
                      >
                        <span
                          className={`inline-flex items-center gap-1.5 text-[12.5px] font-bold ${
                            on ? "text-[#1466D8]" : "text-[#14263D]"
                          }`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full flex-none" style={{ backgroundColor: dot }} />
                          {o.label}
                        </span>
                        {isSale && on ? (
                          // Buy/Loan sits INSIDE the selected For-sale card (4b)
                          <span className="flex gap-1.5 mt-[3px]" onClick={(e) => e.stopPropagation()}>
                            {[
                              { k: "buy", v: buy, set: setBuy, label: "Buy" },
                              { k: "loan", v: loan, set: setLoan, label: "Loan" },
                            ].map((c) => (
                              <span
                                key={c.k}
                                role="checkbox"
                                aria-checked={c.v}
                                tabIndex={0}
                                onClick={() => c.set(!c.v)}
                                onKeyDown={(e) => e.key === "Enter" && c.set(!c.v)}
                                className={`inline-flex items-center gap-[5px] rounded-[7px] px-2 py-[3px] text-[11px] font-semibold cursor-pointer ${
                                  c.v ? "border border-[#BDD9FF] bg-white text-[#1466D8]" : "border border-[#DFE6EF] bg-white text-[#4A5C72]"
                                }`}
                              >
                                {c.v && (
                                  <span className="inline-flex w-3 h-3 rounded-[4px] bg-[#2283FF] text-white text-[8px] font-bold items-center justify-center">
                                    ✓
                                  </span>
                                )}
                                {c.label}
                              </span>
                            ))}
                          </span>
                        ) : (
                          <span className="text-[11px] text-[#8A98AC] leading-[1.45]">{o.help}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {forSale && !step2Valid && <p className="mt-1.5 text-[11.5px] text-[#C4402F]">Pick at least one of Buy / Loan.</p>}
              </div>

              {/* Level · Ready to Fly · Short-term (one 4b row on desktop, 8e rows on mobile) */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-[18px]">
                <div className="flex-none">
                  <FieldLabel suffix="· prefilled from profile">Level</FieldLabel>
                  <div className="inline-flex rounded-[10px] bg-[#EAEFF5] p-[3px]">
                    {["professional", "youth"].map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCategory(c)}
                        className={`px-4 py-1.5 rounded-[7px] text-[12.5px] capitalize ${
                          category === c
                            ? "bg-white font-semibold text-[#14263D] shadow-[0_1px_2px_rgba(16,24,40,.08)]"
                            : "font-medium text-[#5A6B82]"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Desktop: label above switch + caption */}
                <div className="hidden sm:block">
                  <FieldLabel>Ready to Fly</FieldLabel>
                  <div className="flex items-center gap-[9px] pt-1">
                    <Switch on={readyToFly} onChange={setReadyToFly} />
                    <span className="text-[12px] text-[#5A6B82]">Can relocate abroad</span>
                  </div>
                </div>
                <div className="hidden sm:block">
                  <FieldLabel>Short-term contract</FieldLabel>
                  <div className="flex items-center gap-[9px] pt-1">
                    <Switch on={shortTerm} onChange={setShortTerm} />
                    <span className="text-[12px] text-[#5A6B82]">Open to deals under 12 months</span>
                  </div>
                </div>
                {/* Mobile (8e): bordered switch rows */}
                <div className="sm:hidden flex flex-col gap-2">
                  <div className="flex items-center gap-2.5 rounded-xl border border-[#E4E9F1] px-3.5 py-[11px]">
                    <span className="flex-1 text-[13px] font-semibold text-[#14263D]">Ready to Fly</span>
                    <Switch on={readyToFly} onChange={setReadyToFly} />
                  </div>
                  <div className="flex items-center gap-2.5 rounded-xl border border-[#E4E9F1] px-3.5 py-[11px]">
                    <span className="flex-1 text-[13px] font-semibold text-[#14263D]">Open to short-term contract</span>
                    <Switch on={shortTerm} onChange={setShortTerm} />
                  </div>
                </div>
              </div>

              <div>
                <FieldLabel info>Player is not ready for</FieldLabel>
                <CountryBlacklistField value={blacklist} onChange={setBlacklist} />
              </div>

              <div>
                <FieldLabel info suffix="· optional, always shown as a band">
                  Salary expectation
                </FieldLabel>
                {/* 4b: presets as an inline pill row + the dashed Custom pill */}
                <div className="flex flex-wrap items-center gap-[7px]">
                  {SALARY_BAND_PRESETS.map((p) => {
                    const on = salaryPresetId === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setSalaryFrame(on ? null : { ...p.payload })}
                        className={`rounded-full px-3 py-1.5 text-[12px] ${
                          on
                            ? "border-[1.5px] border-[#2283FF] bg-[#F0F7FF] font-bold text-[#1466D8]"
                            : "border border-[#DFE6EF] font-semibold text-[#4A5C72] hover:bg-gray-50"
                        }`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                  <FeeFrameSelect
                    value={salaryIsCustom ? salaryFrame : null}
                    onChange={setSalaryFrame}
                    presets={SALARY_BAND_PRESETS}
                    labelPrefix="Custom band"
                    hidePresetSection
                    matchPanelWidth={false}
                    panelMinWidth={300}
                    renderTrigger={({ label }) =>
                      salaryIsCustom ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-[#2283FF] bg-[#F0F7FF] px-3 py-1.5 text-[12px] font-bold text-[#1466D8]">
                          {label}
                          <span className="text-[10px]">▾</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[#C7D3E2] px-3 py-1.5 text-[12px] font-semibold text-[#8A98AC] hover:bg-gray-50">
                          Custom · try “750k” <span className="text-[10px]">EUR ▾</span>
                        </span>
                      )
                    }
                  />
                </div>
              </div>

              <div>
                <FieldLabel suffix="· optional">One line of context</FieldLabel>
                {/* Bordered field with the live counter inside (4b "41 / 90") */}
                <div className="flex items-end gap-2 rounded-xl border border-[#DFE6EF] px-3.5 py-[11px] focus-within:border-[#2283FF] focus-within:shadow-[0_0_0_3px_rgba(34,131,255,.1)]">
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 90))}
                    rows={1}
                    placeholder="Ready for a step up, club open to talks."
                    className="flex-1 resize-none bg-transparent text-[13px] text-[#243B55] placeholder:text-[#9AA8BC] focus:outline-none"
                  />
                  <span className="text-[11px] text-[#98A5B8] flex-none">{note.length} / 90</span>
                </div>
              </div>
            </div>
          ) : step === 2 ? (
            // 4c / 8f — discretion & visibility
            <div className="space-y-4">
              <div>
                <FieldLabel info>How should the profile appear?</FieldLabel>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {[
                    {
                      on: !anonymized,
                      set: () => setAnonymized(false),
                      title: "Publish named",
                      body: "Name and photo visible to everyone who can see the listing.",
                    },
                    {
                      on: anonymized,
                      set: () => setAnonymized(true),
                      title: "Anonymized profile first",
                      body: "Position, age, market value and salary band only; a named player can be traced. You reveal the name per club, on real interest.",
                    },
                  ].map((o) => (
                    <button
                      key={o.title}
                      type="button"
                      onClick={o.set}
                      className={`text-left rounded-[14px] p-3.5 flex gap-2.5 ${
                        o.on
                          ? "border-[1.5px] border-[#2283FF] bg-[#F7FAFF] shadow-[0_0_0_3px_rgba(34,131,255,.1)]"
                          : "border border-[#DFE6EF] hover:bg-gray-50"
                      }`}
                    >
                      <span
                        className={`w-4 h-4 rounded-full flex-none mt-px ${
                          o.on ? "border-[5px] border-[#2283FF]" : "border-[1.5px] border-[#C7D3E2]"
                        }`}
                      />
                      <span className="min-w-0">
                        <span className={`block text-[13px] font-bold ${o.on ? "text-[#1466D8]" : "text-[#14263D]"}`}>
                          {o.title}
                        </span>
                        <span className={`block text-[11.5px] leading-[1.5] mt-[3px] ${o.on ? "text-[#5A6B82]" : "text-[#8A98AC]"}`}>
                          {o.body}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <FieldLabel info>Who can see it?</FieldLabel>
                {/* 4c: stacked full-width radio rows */}
                <div className="flex flex-col gap-2">
                  {VISIBILITY_OPTIONS.map((o) => {
                    const on = visibility === o.value;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => setVisibility(o.value)}
                        className={`w-full flex items-center gap-2.5 rounded-xl px-3.5 py-3 text-left ${
                          on
                            ? "border-[1.5px] border-[#2283FF] bg-[#F7FAFF] shadow-[0_0_0_3px_rgba(34,131,255,.1)]"
                            : "border border-[#DFE6EF] hover:bg-gray-50"
                        }`}
                      >
                        <span
                          className={`w-4 h-4 rounded-full flex-none ${
                            on ? "border-[5px] border-[#2283FF]" : "border-[1.5px] border-[#C7D3E2]"
                          }`}
                        />
                        <span className="min-w-0 text-[13px]">
                          <span className={`font-bold ${on ? "text-[#1466D8]" : "text-[#14263D]"}`}>{o.label}</span>
                          <span className={`text-[11.5px] ${on ? "text-[#5A6B82]" : "text-[#8A98AC]"}`}>
                            , {o.help.charAt(0).toLowerCase() + o.help.slice(1).replace(/\.$/, "")}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Trust banner (4c) */}
              <div className="flex items-start gap-2.5 rounded-xl border border-[#CFE3FB] bg-[#F0F7FF] px-3.5 py-3">
                <span className="inline-flex w-5 h-5 rounded-full bg-[#2283FF] text-white text-[10px] font-bold items-center justify-center flex-none">
                  ✓
                </span>
                <p className="text-[12.5px] text-[#1E4E96] leading-[1.55]">
                  <b>The player’s current club will never see this listing.</b> Enforced by the platform, not a setting,
                  not an exception.
                </p>
              </div>
            </div>
          ) : (
            // 4d / 8g — preview both variants
            <div className="space-y-4">
              {anonymized ? (
                <>
                  {/* Mobile toggle (8g) */}
                  <div className="sm:hidden flex rounded-[11px] bg-[#EAEFF5] p-[3px]">
                    {[
                      { key: "anon", label: "Anonymized · live" },
                      { key: "named", label: "Named · after reveal" },
                    ].map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setPreviewSide(t.key)}
                        className={`flex-1 py-2 rounded-lg text-[12px] text-center ${
                          previewSide === t.key
                            ? "bg-white font-semibold text-[#14263D] shadow-[0_1px_2px_rgba(16,24,40,.08)]"
                            : "font-medium text-[#5A6B82]"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div className={previewSide === "anon" ? "" : "hidden sm:block"}>
                      <p className="hidden sm:block text-[11px] font-bold uppercase tracking-[0.08em] text-[#1466D8] mb-2">
                        Live at publish
                      </p>
                      <ListingCard listing={anonPreview} primary={<div className="h-9 rounded-lg bg-[#EAEFF5]" />} />
                    </div>
                    <div className={previewSide === "named" ? "" : "hidden sm:block"}>
                      <p className="hidden sm:block text-[11px] font-bold uppercase tracking-[0.08em] text-[#8A98AC] mb-2">
                        After you reveal · per club
                      </p>
                      <ListingCard listing={namedPreview} primary={<div className="h-9 rounded-lg bg-[#EAEFF5]" />} />
                    </div>
                  </div>
                </>
              ) : (
                <div className="sm:max-w-[320px]">
                  <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#1466D8] mb-2">Live at publish</p>
                  <ListingCard listing={namedPreview} primary={<div className="h-9 rounded-lg bg-[#EAEFF5]" />} />
                </div>
              )}
              <p className="text-[11.5px] text-[#8A98AC] text-center sm:hidden">
                Live for 14 days. Extend anytime with one click.
              </p>
            </div>
          )}
        </div>

        {/* Footer nav (hidden once published — 4e carries its own buttons) */}
        {!published && (
          <div className="flex items-center gap-2.5 px-[18px] py-3 pb-6 sm:p-5 sm:py-4 border-t border-[#EEF1F6]">
            {step > 0 ? (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="px-1.5 text-[12.5px] sm:text-[12.5px] font-semibold text-[#5A6B82] hover:text-[#14263D] flex-none"
              >
                ← Back
              </button>
            ) : (
              <span className="hidden sm:block" />
            )}
            {step === 3 && (
              <span className="hidden sm:inline ml-auto text-[11.5px] text-[#8A98AC]">
                Live for 14 days. Extend anytime with one click.
              </span>
            )}
            {step < 3 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={(step === 0 && !canContinueStep1) || (step === 1 && !step2Valid)}
                className="flex-1 sm:flex-none sm:ml-auto rounded-[10px] sm:rounded-[10px] bg-[#2283FF] px-5 sm:px-[22px] py-3 sm:py-2 text-[14px] sm:text-[13px] font-semibold text-white text-center hover:bg-[#1B6FE0] disabled:bg-[#E4E9F1] disabled:text-[#9AA8BC]"
              >
                Continue
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting}
                className={`flex-1 sm:flex-none rounded-[10px] bg-[#2283FF] px-5 sm:px-6 py-3 sm:py-2.5 text-[14px] sm:text-[13px] font-semibold text-white text-center shadow-[0_2px_6px_rgba(34,131,255,.35)] hover:bg-[#1B6FE0] disabled:opacity-60 ${
                  step === 3 ? "" : "sm:ml-auto"
                }`}
              >
                {submitting ? "Publishing…" : isEdit ? "Save changes" : "Publish listing"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListingComposer;
